import { useState, useEffect, useCallback, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import {
  CreditCard,
  Wallet,
  Clock,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  MessageSquare,
  HelpCircle,
  ExternalLink,
  CheckCircle,
  Mail
} from 'lucide-react';

import { SMS_PRICING_TIERS, SMS_BUNDLE_OPTIONS as TOPUP_PACKAGES, calculateCustomAmount } from '../lib/sms-pricing';
import { EMAIL_BUNDLE_OPTIONS, calculateCustomEmailAmount } from '../lib/email-pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GST_RATE = 0.10; // 10% GST for Australia

// ============================================
// SMS Top Up Modal
// ============================================
function TopUpModal({ isOpen, onClose, getToken, onSuccess }) {
  const [packages, setPackages] = useState(TOPUP_PACKAGES);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/topup-packages?channel=sms`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.packages && data.packages.length > 0) {
          setPackages(data.packages.map(p => ({
            amountCents: p.amount_cents,
            label: p.label,
            smsEstimate: p.sms_estimate,
            pricePerSms: `$${(p.amount_cents / 100 / p.sms_estimate).toFixed(3)}`,
            tag: p.tag
          })));
        }
      }
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  };

  const selectAmount = (amountCents) => {
    setSelectedAmount(amountCents);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomInput = (value) => {
    setCustomAmount(value);
    setIsCustom(true);
    setSelectedAmount(null);
  };

  const getCustomAmountDetails = () => {
    return calculateCustomAmount(parseFloat(customAmount) || 0);
  };

  const handleTopUp = async () => {
    setLoading(true);
    setError(null);

    try {
      const amountCents = isCustom
        ? Math.round(parseFloat(customAmount) * 100)
        : selectedAmount;

      if (!amountCents || amountCents < 2500) {
        setError('Minimum top-up is $25');
        setLoading(false);
        return;
      }

      if (amountCents > 10000000) {
        setError('Maximum self-serve top-up is $100,000. Contact us for enterprise volumes.');
        setLoading(false);
        return;
      }

      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-topup-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount_cents: amountCents, channel: 'sms' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const customDetails = getCustomAmountDetails();
  const selectedPackage = packages.find(p => p.amountCents === selectedAmount);

  const displayAmount = isCustom ? parseFloat(customAmount) || 0 : (selectedAmount || 0) / 100;
  const gstAmount = displayAmount * GST_RATE;
  const totalWithGst = displayAmount + gstAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Top up SMS credits</h2>
            <p className="text-sm text-gray-500 mt-1">
              Choose a prepaid SMS bundle below or enter a custom amount
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Prepaid SMS Bundles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.amountCents}
                  onClick={() => selectAmount(pkg.amountCents)}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    selectedAmount === pkg.amountCents
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {pkg.tag && (
                    <span className="absolute -top-2 left-3 px-2 py-0.5 bg-orange-500 text-white text-xs font-medium rounded-full">
                      {pkg.tag}
                    </span>
                  )}
                  <div className="text-2xl font-bold text-gray-900">{pkg.label}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {pkg.smsEstimate.toLocaleString()} SMS credits
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {pkg.pricePerSms} per SMS (locked in)
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom amount</h3>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => handleCustomInput(e.target.value)}
                  placeholder="25.00"
                  min="25"
                  max="100000"
                  className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    isCustom ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                />
              </div>
              {isCustom && parseFloat(customAmount) >= 25 && (
                <div className="text-sm text-gray-600">
                  {customDetails.smsEstimate.toLocaleString()} SMS @ {customDetails.pricePerSms}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Minimum: $25</p>
          </div>

          {(selectedAmount || (isCustom && parseFloat(customAmount) >= 25)) && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {selectedPackage && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                    <div className="text-lg font-bold text-orange-900">
                      {selectedPackage.smsEstimate.toLocaleString()} SMS Credits
                    </div>
                    <div className="text-xs text-orange-700 mt-1">
                      Locked-in rate: {selectedPackage.pricePerSms}/SMS
                    </div>
                  </div>
                )}
                {isCustom && parseFloat(customAmount) >= 25 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                    <div className="text-lg font-bold text-orange-900">
                      {customDetails.smsEstimate.toLocaleString()} SMS Credits
                    </div>
                    <div className="text-xs text-orange-700 mt-1">
                      Locked-in rate: {customDetails.pricePerSms}/SMS
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Payment amount</span>
                  <span className="font-medium">${displayAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST (10%)</span>
                  <span className="font-medium">${gstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                  <span className="font-semibold text-gray-900">Total to pay</span>
                  <span className="font-bold text-gray-900">${totalWithGst.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            All prices are in AUD and exclude GST. GST will be added at checkout.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleTopUp}
              disabled={loading || (!selectedAmount && !isCustom) || (isCustom && parseFloat(customAmount) < 25)}
              className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================
// Email Top Up Modal
// ============================================
function EmailTopUpModal({ isOpen, onClose, getToken, onSuccess }) {
  const [packages] = useState(EMAIL_BUNDLE_OPTIONS);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectAmount = (amountCents) => {
    setSelectedAmount(amountCents);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomInput = (value) => {
    setCustomAmount(value);
    setIsCustom(true);
    setSelectedAmount(null);
  };

  const getCustomAmountDetails = () => {
    return calculateCustomEmailAmount(parseFloat(customAmount) || 0);
  };

  const handleTopUp = async () => {
    setLoading(true);
    setError(null);

    try {
      const amountCents = isCustom
        ? Math.round(parseFloat(customAmount) * 100)
        : selectedAmount;

      if (!amountCents || amountCents < 1000) {
        setError('Minimum top-up is $10');
        setLoading(false);
        return;
      }

      if (amountCents > 10000000) {
        setError('Maximum self-serve top-up is $100,000.');
        setLoading(false);
        return;
      }

      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-topup-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount_cents: amountCents, channel: 'email' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const customDetails = getCustomAmountDetails();
  const selectedPackage = packages.find(p => p.amountCents === selectedAmount);

  const displayAmount = isCustom ? parseFloat(customAmount) || 0 : (selectedAmount || 0) / 100;
  const gstAmount = displayAmount * GST_RATE;
  const totalWithGst = displayAmount + gstAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Top up email credits</h2>
            <p className="text-sm text-gray-500 mt-1">
              Flat rate: $0.01 per email. Choose a bundle or enter a custom amount.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Email Credit Bundles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.amountCents}
                  onClick={() => selectAmount(pkg.amountCents)}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    selectedAmount === pkg.amountCents
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {pkg.tag && (
                    <span className="absolute -top-2 left-3 px-2 py-0.5 bg-purple-500 text-white text-xs font-medium rounded-full">
                      {pkg.tag}
                    </span>
                  )}
                  <div className="text-2xl font-bold text-gray-900">{pkg.label}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {pkg.emailEstimate.toLocaleString()} email credits
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {pkg.pricePerEmail} per email
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom amount</h3>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => handleCustomInput(e.target.value)}
                  placeholder="10.00"
                  min="10"
                  max="100000"
                  className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    isCustom ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}
                />
              </div>
              {isCustom && parseFloat(customAmount) >= 10 && (
                <div className="text-sm text-gray-600">
                  {customDetails.emailEstimate.toLocaleString()} emails @ {customDetails.pricePerEmail}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Minimum: $10</p>
          </div>

          {(selectedAmount || (isCustom && parseFloat(customAmount) >= 10)) && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {selectedPackage && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                    <div className="text-lg font-bold text-purple-900">
                      {selectedPackage.emailEstimate.toLocaleString()} Email Credits
                    </div>
                    <div className="text-xs text-purple-700 mt-1">
                      Flat rate: {selectedPackage.pricePerEmail}/email
                    </div>
                  </div>
                )}
                {isCustom && parseFloat(customAmount) >= 10 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                    <div className="text-lg font-bold text-purple-900">
                      {customDetails.emailEstimate.toLocaleString()} Email Credits
                    </div>
                    <div className="text-xs text-purple-700 mt-1">
                      Flat rate: {customDetails.pricePerEmail}/email
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Payment amount</span>
                  <span className="font-medium">${displayAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST (10%)</span>
                  <span className="font-medium">${gstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                  <span className="font-semibold text-gray-900">Total to pay</span>
                  <span className="font-bold text-gray-900">${totalWithGst.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            All prices are in AUD and exclude GST. GST will be added at checkout.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleTopUp}
              disabled={loading || (!selectedAmount && !isCustom) || (isCustom && parseFloat(customAmount) < 10)}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================
// Main WalletPage Component
// ============================================
export default function WalletPage() {
  const { getToken } = useTenant();
  const {
    walletStatus: preloadedWallet,
    refreshWallet: contextRefreshWallet,
    emailBalance: preloadedEmailBalance,
    refreshEmailBalance: contextRefreshEmailBalance,
  } = useDashboardData() || {};

  // Tab state: 'overview' | 'sms' | 'email'
  const [activeTab, setActiveTab] = useState('overview');

  const [smsBalance, setSmsBalance] = useState(null);
  const [emailBalance, setEmailBalance] = useState(preloadedEmailBalance || null);
  const [wallet, setWallet] = useState(preloadedWallet ? { balance_cents: preloadedWallet.wallet_balance_cents } : null);
  const [transactions, setTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(!preloadedWallet);
  const [smsBalanceLoading, setSmsBalanceLoading] = useState(true);
  const [emailBalanceLoading, setEmailBalanceLoading] = useState(!preloadedEmailBalance);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showEmailTopUp, setShowEmailTopUp] = useState(false);
  const [showPricingTooltip, setShowPricingTooltip] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoSuccess, setPromoSuccess] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [topUpSuccess, setTopUpSuccess] = useState(false);
  const pollRef = useRef(null);

  const fetchSmsBalance = useCallback(async () => {
    setSmsBalanceLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/wallet/sms-balance`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch SMS balance');
      const data = await response.json();
      setSmsBalance(data);
    } catch (err) {
      console.error('SMS balance fetch error:', err);
      setError(err.message);
    } finally {
      setSmsBalanceLoading(false);
    }
  }, [getToken]);

  const fetchEmailBalance = useCallback(async () => {
    setEmailBalanceLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/wallet/email-balance`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch email balance');
      const data = await response.json();
      setEmailBalance(data);
    } catch (err) {
      console.error('Email balance fetch error:', err);
    } finally {
      setEmailBalanceLoading(false);
    }
  }, [getToken]);

  const fetchWallet = useCallback(async () => {
    setWalletLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/wallet`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch wallet');
      const data = await response.json();
      setWallet(data.wallet);
    } catch (err) {
      setError(err.message);
    } finally {
      setWalletLoading(false);
    }
  }, [getToken]);

  const fetchTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ page: page.toString(), page_size: '10' });
      const response = await fetch(`${BACKEND_URL}/api/wallet/transactions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch transactions');
      const data = await response.json();
      setTransactions(data.transactions);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setTransactionsLoading(false);
    }
  }, [getToken, page]);

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }
    setPromoLoading(true);
    setPromoError(null);
    setPromoSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/promo-codes/redeem`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to redeem promo code');
      setPromoSuccess({ smsAdded: data.sms_added, message: data.message });
      setPromoCode('');
      await fetchSmsBalance();
      await fetchTransactions();
    } catch (err) {
      setPromoError(err.message);
    } finally {
      setPromoLoading(false);
    }
  };

  useEffect(() => {
    fetchSmsBalance();
    fetchEmailBalance();
    if (!preloadedWallet) fetchWallet();
    fetchTransactions();
  }, [fetchSmsBalance, fetchEmailBalance, fetchWallet, fetchTransactions, preloadedWallet]);

  // Detect return from Stripe checkout and poll for updated balance
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') === 'success') {
      setTopUpSuccess(true);
      const returnChannel = params.get('channel') || 'sms';
      // Switch to the appropriate tab
      if (returnChannel === 'email') setActiveTab('email');
      else setActiveTab('sms');

      window.history.replaceState({}, '', '/dashboard/wallet');

      let attempts = 0;
      const maxAttempts = 10;
      pollRef.current = setInterval(async () => {
        attempts++;
        await fetchSmsBalance();
        await fetchEmailBalance();
        await fetchWallet();
        await fetchTransactions();
        if (contextRefreshWallet) contextRefreshWallet();
        if (contextRefreshEmailBalance) contextRefreshEmailBalance();
        if (attempts >= maxAttempts && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 3000);

      setTimeout(() => setTopUpSuccess(false), 10000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const totalSmsRemaining = smsBalance?.total_sms_remaining || 0;
  const totalEmailsRemaining = emailBalance?.total_emails_remaining || 0;

  // ============================================
  // Tab: Overview
  // ============================================
  const renderOverview = () => (
    <>
      {/* Two Balance Cards Side-by-Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* SMS Balance Card */}
        <button
          onClick={() => setActiveTab('sms')}
          className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white text-left hover:from-orange-600 hover:to-orange-700 transition-all"
        >
          <div className="flex items-center gap-2 text-orange-100 mb-2">
            <MessageSquare className="w-5 h-5" />
            <span className="text-sm font-medium">SMS Credits</span>
          </div>
          {smsBalanceLoading ? (
            <div className="h-10 w-32 bg-white/30 rounded-lg animate-pulse"></div>
          ) : (
            <div className="text-3xl font-bold">
              {totalSmsRemaining.toLocaleString()} <span className="text-lg font-normal text-orange-200">SMS</span>
            </div>
          )}
          <p className="text-orange-100 mt-2 text-sm">Click to manage SMS credits</p>
        </button>

        {/* Email Balance Card */}
        <button
          onClick={() => setActiveTab('email')}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white text-left hover:from-purple-600 hover:to-purple-700 transition-all"
        >
          <div className="flex items-center gap-2 text-purple-100 mb-2">
            <Mail className="w-5 h-5" />
            <span className="text-sm font-medium">Email Credits</span>
          </div>
          {emailBalanceLoading ? (
            <div className="h-10 w-32 bg-white/30 rounded-lg animate-pulse"></div>
          ) : (
            <div className="text-3xl font-bold">
              {totalEmailsRemaining.toLocaleString()} <span className="text-lg font-normal text-purple-200">Emails</span>
            </div>
          )}
          <p className="text-purple-100 mt-2 text-sm">Click to manage email credits</p>
        </button>
      </div>

      {/* Promo Code Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6 mb-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Have a promo code?</h3>
            <p className="text-sm text-gray-600 mt-1">Redeem your code to get free SMS credits instantly</p>
          </div>
        </div>

        {promoSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-green-800">{promoSuccess.message}</p>
              <p className="text-sm text-green-700 mt-1">
                {promoSuccess.smsAdded.toLocaleString()} SMS credits have been added
              </p>
            </div>
            <button onClick={() => setPromoSuccess(null)} className="text-green-600 hover:text-green-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {promoError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1"><p className="text-red-800 text-sm">{promoError}</p></div>
            <button onClick={() => setPromoError(null)} className="text-red-600 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null); setPromoSuccess(null); }}
              placeholder="Enter promo code (e.g., WELCOME2024)"
              maxLength={20}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-mono uppercase"
              disabled={promoLoading}
              onKeyDown={(e) => { if (e.key === 'Enter' && !promoLoading) handleRedeemPromo(); }}
            />
          </div>
          <button
            onClick={handleRedeemPromo}
            disabled={promoLoading || !promoCode.trim()}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 min-w-[140px]"
          >
            {promoLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Redeeming...</>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Redeem Code
              </>
            )}
          </button>
        </div>
      </div>

      {/* Transaction History */}
      {renderTransactionHistory()}
    </>
  );


  // ============================================
  // Tab: SMS Credits
  // ============================================
  const renderSmsTab = () => (
    <>
      {/* SMS Balance Card */}
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 lg:p-8 text-white mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-orange-100 mb-2">
              <Wallet className="w-5 h-5" />
              <span className="text-sm font-medium">SMS Balance</span>
            </div>
            {smsBalanceLoading ? (
              <div className="h-12 w-40 bg-white/30 rounded-lg animate-pulse"></div>
            ) : (
              <>
                <div className="text-4xl lg:text-5xl font-bold" data-testid="sms-balance">
                  {totalSmsRemaining.toLocaleString()} <span className="text-xl font-normal text-orange-200">SMS</span>
                </div>
                {smsBalance?.pack_count > 0 && (
                  <p className="text-orange-100 mt-2 text-sm">
                    Across {smsBalance.pack_count} {smsBalance.pack_count === 1 ? 'pack' : 'packs'}
                  </p>
                )}
              </>
            )}
          </div>
          <OnboardingTooltip id="wallet_topup" text="Add SMS credits to start sending. No subscription needed.">
            <button
              onClick={() => setShowTopUp(true)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-orange-600 rounded-xl font-semibold hover:bg-orange-50 transition-colors"
              data-testid="top-up-btn"
            >
              <Plus className="w-5 h-5" />
              Add SMS Credits
            </button>
          </OnboardingTooltip>
        </div>
      </div>

      {/* Empty state */}
      {!smsBalanceLoading && totalSmsRemaining === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-8 h-8 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No SMS credits remaining</h3>
          <p className="text-yellow-700 mb-4">
            Add SMS credits to start sending campaigns. Credits never expire.
          </p>
          <button
            onClick={() => setShowTopUp(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700"
          >
            <Plus className="w-4 h-4" /> Add SMS credits
          </button>
        </div>
      )}

      {/* SMS Pricing Tiers */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">How SMS bundles work</h2>
        <p className="text-sm text-gray-600 mb-4">
          Prepaid SMS packs with locked-in rates. The more you buy, the better your rate.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {SMS_PRICING_TIERS.map((tier, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600 mb-1">{tier.label}</div>
              <div className="text-xl font-bold text-gray-900">${tier.pricePerSms.toFixed(3)}</div>
              <div className="text-xs text-gray-500">per SMS</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Your SMS rate is locked in when you purchase. All prices are in AUD ex GST.
        </p>
      </div>

      {/* Custom Sender ID */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800"><strong>Want your business name as the sender?</strong></p>
            <p className="text-sm text-blue-700 mt-1">
              Set up a Custom Sender ID for $299 + GST.{' '}
              <a href="/#contact" className="underline hover:no-underline">Contact us</a>
            </p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
        <h3 className="font-semibold text-green-800 mb-3">Prepaid SMS credits that work on your terms</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {['Credits never expire', 'Rates locked at purchase', 'No subscriptions', 'No hidden fees', 'Pay only for SMS sent'].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // ============================================
  // Tab: Email Credits
  // ============================================
  const renderEmailTab = () => (
    <>
      {/* Email Balance Card */}
      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 lg:p-8 text-white mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-purple-100 mb-2">
              <Mail className="w-5 h-5" />
              <span className="text-sm font-medium">Email Balance</span>
            </div>
            {emailBalanceLoading ? (
              <div className="h-12 w-40 bg-white/30 rounded-lg animate-pulse"></div>
            ) : (
              <>
                <div className="text-4xl lg:text-5xl font-bold" data-testid="email-balance">
                  {totalEmailsRemaining.toLocaleString()} <span className="text-xl font-normal text-purple-200">Emails</span>
                </div>
                {emailBalance?.pack_count > 0 && (
                  <p className="text-purple-100 mt-2 text-sm">
                    Across {emailBalance.pack_count} {emailBalance.pack_count === 1 ? 'pack' : 'packs'}
                  </p>
                )}
              </>
            )}
          </div>
          <button
            onClick={() => setShowEmailTopUp(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition-colors"
            data-testid="email-top-up-btn"
          >
            <Plus className="w-5 h-5" />
            Add Email Credits
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!emailBalanceLoading && totalEmailsRemaining === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No email credits remaining</h3>
          <p className="text-yellow-700 mb-4">
            Add email credits to start sending email campaigns. Credits never expire.
          </p>
          <button
            onClick={() => setShowEmailTopUp(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700"
          >
            <Plus className="w-4 h-4" /> Add email credits
          </button>
        </div>
      )}

      {/* Email Pricing Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Email credit pricing</h2>
        <p className="text-sm text-gray-600 mb-4">
          Simple flat rate: $0.01 per email. No tiers, no complexity.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {EMAIL_BUNDLE_OPTIONS.map((pkg, index) => (
            <div key={index} className="bg-purple-50 border border-purple-100 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-900">{pkg.label}</div>
              <div className="text-sm text-purple-700 mt-1">{pkg.emailEstimate.toLocaleString()} emails</div>
              <div className="text-xs text-purple-500 mt-1">{pkg.pricePerEmail}/email</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          All prices are in AUD ex GST. Custom amounts available from $10.
        </p>
      </div>

      {/* Benefits */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
        <h3 className="font-semibold text-green-800 mb-3">Prepaid email credits that work on your terms</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {['Credits never expire', 'Flat rate: $0.01/email', 'No subscriptions', 'No hidden fees', 'Pay only for emails sent'].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );


  // ============================================
  // Transaction History (shared)
  // ============================================
  const renderTransactionHistory = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
        <p className="text-sm text-gray-500 mt-1">All wallet top-ups and credit usage</p>
      </div>

      {transactionsLoading ? (
        <div className="p-8 text-center">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
          <p className="text-gray-500 mb-4">
            Your transaction history will appear here once you top up your wallet or send campaigns.
          </p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-100">
            {transactions.map((txn) => (
              <div key={txn.id} className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    txn.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {txn.type === 'credit' ? (
                      <ArrowUpRight className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{txn.description}</div>
                    <div className="text-sm text-gray-500">{formatDate(txn.created_at)}</div>
                  </div>
                </div>
                <div className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                  {txn.type === 'credit' ? '+' : '-'}${(txn.amount_cents / 100).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );


  // ============================================
  // Render
  // ============================================
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'sms', label: 'SMS Credits' },
    { id: 'email', label: 'Email Credits' },
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="wallet-page">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet</h1>
        <p className="text-gray-600 mt-1">Manage your SMS and email credits</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 bg-gray-100 rounded-lg p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {topUpSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-green-700">Payment received! Your credits are being added — this may take a few seconds.</span>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'sms' && renderSmsTab()}
      {activeTab === 'email' && renderEmailTab()}

      {/* Modals */}
      <TopUpModal
        isOpen={showTopUp}
        onClose={() => setShowTopUp(false)}
        getToken={getToken}
        onSuccess={() => {
          setShowTopUp(false);
          fetchSmsBalance();
          fetchWallet();
          fetchTransactions();
        }}
      />
      <EmailTopUpModal
        isOpen={showEmailTopUp}
        onClose={() => setShowEmailTopUp(false)}
        getToken={getToken}
        onSuccess={() => {
          setShowEmailTopUp(false);
          fetchEmailBalance();
          fetchWallet();
          fetchTransactions();
        }}
      />
    </div>
  );
}
